import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useIsAuthenticated } from '@/lib/auth';
import { useFavourites } from '@/lib/favourites';
import { useCategoryMap } from '@/lib/useCategoryMap';
import { dailySeed, shuffled } from '@/lib/daily';
import { buildSections } from '@/lib/sections';
import TopBar from '@/components/TopBar';
import CategoryStories from '@/components/CategoryStories';
import CategorySection from '@/components/CategorySection';
import RecipeMosaic from '@/components/RecipeMosaic';
import { normalizeForSearch } from '@shared/search';

// The unfiltered home feed is capped at three phone rows (#7): the staggered
// opening block (3) plus two plain rows of two. Everything below it is category
// sections instead of an endless grid.
const FEED_SIZE = 7;

export default function RecipeList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('category');
  const isAuthenticated = useIsAuthenticated();
  const { isFavourite } = useFavourites();
  const { categories, byId: categoriesById } = useCategoryMap();
  const [query, setQuery] = useState('');

  const recipesQuery = useQuery({
    queryKey: ['recipes', activeSlug],
    queryFn: () => api.listRecipes(activeSlug ?? undefined),
  });

  const setCategory = (slug: string | null) => setSearchParams(slug ? { category: slug } : {});
  const fav = (id: number) => isAuthenticated && isFavourite(id);

  // No category selected → shuffle the whole feed with a day-stable seed (#6), so
  // the order stays the same across refreshes and only changes each new day; a
  // category fixes the order to newest-first (the server already sorts that way).
  // Typing in the search box keeps the order stable.
  const ordered = useMemo(() => {
    const list = recipesQuery.data ?? [];
    return activeSlug ? list : shuffled(list, dailySeed(new Date()));
  }, [recipesQuery.data, activeSlug]);

  // Accent-insensitive match against the server-built search blob (#2).
  const nq = normalizeForSearch(query);
  const recipes = ordered.filter((r) => !nq || r.search.includes(nq));
  const filtering = nq.length > 0 || !!activeSlug;

  // Category sections below the feed (#7). Only for the plain home view — a
  // search or an active category falls back to one flat result list.
  const sections = useMemo(
    () => (filtering ? [] : buildSections(recipesQuery.data ?? [], categories, new Date())),
    [recipesQuery.data, categories, filtering],
  );

  // With sections to browse, the feed is a teaser capped to FEED_SIZE (per
  // breakpoint, RecipeMosaic rounds that up to a full row); without any (too
  // few recipes yet), keep showing everything so nothing becomes unreachable.
  const feedCap = sections.length > 0 ? FEED_SIZE : undefined;

  return (
    <div className="mx-auto w-full max-w-[520px] px-5 pb-16 sm:max-w-3xl lg:max-w-5xl">
      <TopBar />

      <div className="mt-3 flex items-center gap-2 rounded-2xl bg-accent-soft px-4 py-3">
        <Search size={18} className="shrink-0 text-text-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Hledat recept nebo surovinu…"
          className="w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
        />
      </div>

      <CategoryStories categories={categories} activeSlug={activeSlug} onChange={setCategory} />

      {recipesQuery.isPending && (
        <div className="grid grid-cols-2 gap-3 pt-5 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-2xl bg-accent-soft" />
          ))}
        </div>
      )}

      {recipesQuery.isError && (
        <p className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Nepodařilo se načíst recepty.
        </p>
      )}

      {recipesQuery.data && recipes.length === 0 && (
        <div className="mt-5 rounded-2xl border border-dashed border-border p-10 text-center text-text-muted">
          {filtering ? (
            // Searching or a category is active and nothing matched (#3): just the
            // headline, no "try another term" subtitle.
            <p className="font-serif text-xl text-text">Nic nenalezeno</p>
          ) : (
            <>
              <p className="font-serif text-xl text-text">Zatím tu nic není</p>
              <p className="mt-1 text-sm">Přidej první recept ve správě.</p>
            </>
          )}
        </div>
      )}

      <RecipeMosaic
        recipes={recipes}
        categoriesById={categoriesById}
        isFavourite={fav}
        className="pt-5"
        feedCap={feedCap}
        priority
      />

      {sections.map((s) => (
        <CategorySection
          key={s.category.id}
          category={s.category}
          recipes={s.recipes}
          total={s.total}
          categoriesById={categoriesById}
          isFavourite={fav}
          onOpenCategory={setCategory}
        />
      ))}
    </div>
  );
}
